import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../utils/helpers';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const PushNotificationModal = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setSupported(false);
    }
  }, []);

  const handleEnable = async () => {
    setLoading(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Permiso de notificaciones denegado');
        onClose();
        return;
      }

      const registration = await navigator.serviceWorker.ready;

      let vapidKey = VAPID_PUBLIC_KEY;
      if (!vapidKey) {
        try {
          const keyRes = await fetch(`${API}/push/vapid-key`);
          if (keyRes.ok) {
            const keyData = await keyRes.json();
            vapidKey = keyData.vapid_key;
          }
        } catch (e) {}
      }

      if (!vapidKey) {
        toast.error('Configuracion de notificaciones no disponible');
        setLoading(false);
        onClose();
        return;
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      const timezoneOffset = new Date().getTimezoneOffset();

      const res = await fetch(`${API}/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          subscription: subscription.toJSON(),
          timezone_offset: timezoneOffset
        })
      });

      if (res.ok) {
        toast.success('Notificaciones activadas!');
        localStorage.setItem('push_modal_shown', 'true');
      } else if (res.status === 401) {
        toast.error('Inicia sesion para activar notificaciones');
      } else {
        toast.error('Error al activar notificaciones');
      }
    } catch (e) {
      console.error('Push subscription error:', e);
      toast.error('No se pudieron activar las notificaciones');
    }
    setLoading(false);
    onClose();
  };

  const handleSkip = () => {
    localStorage.setItem('push_modal_shown', 'true');
    onClose();
  };

  if (!supported) {
    onClose();
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={handleSkip}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-full max-w-md glass-card p-6 border border-gold-300/10"
          onClick={e => e.stopPropagation()}
          data-testid="push-notification-modal"
        >
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 text-[var(--text-muted)] hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center">
              <Bell className="w-8 h-8 text-yellow-300" />
            </div>
            <h2 className="text-xl font-bold text-purple-300 mb-2">
              No te pierdas tu lectura diaria
            </h2>
            <p className="text-[var(--text-muted)] text-sm">
              Activa las notificaciones para recibir tu carta del dia cada manana a las 9 AM y recordatorios especiales en tu cumpleanos.
            </p>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
              <Sparkles className="w-5 h-5 text-purple-400" />
              <span className="text-sm text-[var(--text-secondary)]">Carta del dia a las 9 AM</span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
              <Sparkles className="w-5 h-5 text-purple-400" />
              <span className="text-sm text-[var(--text-secondary)]">Lectura especial de cumpleanos</span>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleSkip}
              className="flex-1 py-3 rounded-lg text-sm font-medium text-[var(--text-muted)] hover:text-white transition-colors"
              data-testid="push-modal-skip"
            >
              Ahora no
            </button>
            <button
              onClick={handleEnable}
              disabled={loading}
              className="flex-1 btn-gold py-3"
              data-testid="push-modal-enable"
            >
              {loading ? 'Activando...' : 'Activar'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PushNotificationModal;
